import getConfig from 'next/config';

import { getMParticleEventType } from 'tracking/utils/getMParticleEventType';
import { includeGlobalEventAttributes } from 'tracking/utils/includeGlobalEventAttributes';
import { checkAttributesAgainstSchema } from 'tracking/utils/checkAttributesAgainstSchema';
import { getMParticle } from 'tracking/utils/getMParticle';
import goatWebEvents from 'analytics-event-schema/web_outputs/goat_web/schema.json';
import { IEventSchema, IEventAttributes } from 'tracking/types/Event';
import { bugsnag } from 'shared/utils/services/bugsnag';

// Here is how to add a new event
// https://docs.goateng.com/frontend/guides/add-analytics-events/

export const sendTrackingEvent = (eventName: string, eventAttributes?: IEventAttributes) => {
  const {
    publicRuntimeConfig: { RELEASE_ENV },
  } = getConfig();

  if (eventAttributes?.service_name) {
    delete eventAttributes.service_name;
    if (Object.keys(eventAttributes).length === 0) {
      eventAttributes = undefined;
    }
  }

  try {
    const mParticle = getMParticle();

    // Comment out for local event testing
    if (!mParticle || RELEASE_ENV === 'development') {
      return;
    }
    const event: IEventSchema = goatWebEvents?.[eventName.toUpperCase()];

    if (!event) {
      if (RELEASE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(
          [
            `Missing tracking event: ${eventName} does not exist.`,
            "Make sure it's enabled for web in 'analytics-event-schema'.",
          ].join(' '),
        );

        return;
      }

      bugsnag(`sendTrackingEvent: ${eventName} is not present in the event schema object.`, {
        context: 'sendTrackingEvent',
      });

      return;
    }

    const { attributes: schemaAttributes, name: schemaName, type: schemaType } = event;

    if (eventAttributes && !checkAttributesAgainstSchema(eventAttributes, schemaAttributes)) {
      bugsnag('event attributes do not match expected attributes defined in schema', {
        context: 'sendTrackingEvent',
        metadata: { name: eventName, payload: Object.keys(eventAttributes) },
      });
    }

    const eventPayload = includeGlobalEventAttributes(eventAttributes);

    // Uncomment below for easy event tracking logs
    // console.log(`$$$ EVENT ${eventName.toUpperCase()}:`, eventPayload);

    mParticle.logEvent(schemaName, getMParticleEventType(schemaType, mParticle), eventPayload);
  } catch (error) {
    bugsnag(error, {
      context: 'sendTrackingEvent',
    });
  }
};
